/**
 * Split showcase — one-shot media reveal when ≥50% of the section is in the viewport.
 * CSS transitions run at their own pace; no scroll binding, no reverse on scroll away.
 * Runs again only after a full page load (or theme editor section re-insert).
 */
(function () {
  var SEL = '[data-split-showcase-reveal]';
  var REVEALED = 'split-showcase--media-revealed';
  /** After this, CSS drops staggered transition-delay on chips (hover transform stays snappy). */
  var CHIPS_ENTRANCE_DONE = 'split-showcase--chips-entrance-done';
  var CHIPS_STAGGER_DONE_MS = 2000;

  function revealApi() {
    return window.R3NU && window.R3NU.reveal ? window.R3NU.reveal : null;
  }

  function chipsStaggerDoneMs() {
    var api = revealApi();
    return api && api.isMobile() ? 1200 : CHIPS_STAGGER_DONE_MS;
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /** Fraction of the section’s height that intersects the viewport (0..1). */
  function visibleHeightRatio(entry) {
    var r = entry.boundingClientRect;
    var h = r.height;
    if (h <= 0) return 0;
    var vh = window.innerHeight || 1;
    var bottom = Math.min(r.bottom, vh);
    var top = Math.max(r.top, 0);
    var visible = Math.max(0, bottom - top);
    return visible / h;
  }

  function getRevealThreshold() {
    var v = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--r3nu-reveal-threshold'));
    return (isNaN(v) || v <= 0) ? 0.2 : v;
  }

  function shouldReveal(entry) {
    var threshold = getRevealThreshold();
    var r = entry.boundingClientRect;
    var h = Math.max(1, r.height);
    var vh = window.innerHeight || 1;
    var ratio = visibleHeightRatio(entry);
    var need = Math.min(threshold, (vh / h) * 0.98);
    return ratio >= need || entry.intersectionRatio >= threshold;
  }

  function scheduleChipsEntranceDone(root) {
    if (root._splitShowcaseChipsT) {
      clearTimeout(root._splitShowcaseChipsT);
      root._splitShowcaseChipsT = null;
    }
    if (prefersReducedMotion()) {
      root.classList.add(CHIPS_ENTRANCE_DONE);
      return;
    }
    root._splitShowcaseChipsT = setTimeout(function () {
      root._splitShowcaseChipsT = null;
      root.classList.add(CHIPS_ENTRANCE_DONE);
    }, chipsStaggerDoneMs());
  }

  function reveal(root) {
    if (root.classList.contains(REVEALED)) return;
    root.classList.add(REVEALED);
    scheduleChipsEntranceDone(root);
    if (root._splitShowcaseIO) {
      root._splitShowcaseIO.disconnect();
      root._splitShowcaseIO = null;
    }
  }

  function init(root) {
    if (!root || root.dataset.splitShowcaseRevealInit) return;
    root.dataset.splitShowcaseRevealInit = 'true';

    initSplitShowcaseProductCtas(root);

    if (prefersReducedMotion()) {
      reveal(root);
      return;
    }

    if (root.classList.contains(REVEALED)) return;

    var api = revealApi();
    var rootMargin = api ? api.getRootMargin() : '0px 0px 12% 0px';

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting || entry.target !== root) return;
          if (shouldReveal(entry)) reveal(root);
        });
      },
      {
        threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
        rootMargin: rootMargin,
      }
    );

    io.observe(root);
    root._splitShowcaseIO = io;

    function onScrollRaf() {
      if (root.classList.contains(REVEALED)) return;
      requestAnimationFrame(function () {
        if (root.classList.contains(REVEALED)) return;
        try {
          if (root._splitShowcaseIO) {
            root._splitShowcaseIO.takeRecords().forEach(function (entry) {
              if (entry.target === root && entry.isIntersecting && shouldReveal(entry)) reveal(root);
            });
          }
        } catch (e) {}
        if (!root.classList.contains(REVEALED)) {
          var r = root.getBoundingClientRect();
          var fake = { boundingClientRect: r, intersectionRatio: 0, isIntersecting: r.bottom > 0 && r.top < window.innerHeight };
          if (fake.isIntersecting && shouldReveal(fake)) reveal(root);
        }
      });
    }
    root._splitShowcaseOnScroll = onScrollRaf;
    window.addEventListener('scroll', onScrollRaf, { passive: true, capture: true });
    if (window.visualViewport) {
      window.visualViewport.addEventListener('scroll', onScrollRaf, { passive: true });
    }

    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        if (root.classList.contains(REVEALED)) return;
        var r = root.getBoundingClientRect();
        var fake = { boundingClientRect: r, intersectionRatio: 0, isIntersecting: r.bottom > 0 && r.top < window.innerHeight };
        if (fake.isIntersecting && shouldReveal(fake)) reveal(root);
      });
    });
  }

  function normalizePathname(p) {
    if (!p || p === '') return '/';
    var s = p.replace(/\/$/, '') || '/';
    return s;
  }

  function onSplitShowcaseSecondarySamePageClick(root) {
    return function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      var a = t.closest('a.split-showcase__link[data-r3nu-product-secondary]');
      if (!a || !root.contains(a)) return;
      var href = a.getAttribute('href');
      if (!href || href === '#' || href === '') return;
      try {
        var link = new URL(a.getAttribute('href'), window.location.origin);
        if (link.origin !== window.location.origin) return;
        if (normalizePathname(link.pathname) !== normalizePathname(window.location.pathname)) return;
        e.preventDefault();
        e.stopPropagation();
        if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          window.scrollTo(0, 0);
        } else {
          window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        }
      } catch (err) {}
    };
  }

  function initSplitShowcaseProductCtas(root) {
    if (!root || root.dataset.splitShowcaseCtaInit) return;
    root.dataset.splitShowcaseCtaInit = 'true';
    var fn = onSplitShowcaseSecondarySamePageClick(root);
    root._splitShowcaseSamePage = fn;
    root.addEventListener('click', fn, true);
  }

  function destroy(root) {
    if (!root) return;
    if (root._splitShowcaseChipsT) {
      clearTimeout(root._splitShowcaseChipsT);
      root._splitShowcaseChipsT = null;
    }
    if (root._splitShowcaseIO) {
      root._splitShowcaseIO.disconnect();
      root._splitShowcaseIO = null;
    }
    if (root._splitShowcaseOnScroll) {
      window.removeEventListener('scroll', root._splitShowcaseOnScroll, { capture: true });
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('scroll', root._splitShowcaseOnScroll);
      }
      root._splitShowcaseOnScroll = null;
    }
    if (root._splitShowcaseSamePage) {
      root.removeEventListener('click', root._splitShowcaseSamePage, true);
      root._splitShowcaseSamePage = null;
    }
    delete root.dataset.splitShowcaseRevealInit;
    delete root.dataset.splitShowcaseCtaInit;
    root.classList.remove(REVEALED, CHIPS_ENTRANCE_DONE);
  }

  function scan(container) {
    var scope = container || document;
    scope.querySelectorAll(SEL).forEach(init);
  }

  function boot() {
    scan(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (ev) {
    if (ev.target && ev.target.querySelectorAll) scan(ev.target);
  });

  document.addEventListener('shopify:section:unload', function (ev) {
    if (!ev.target || !ev.target.querySelectorAll) return;
    ev.target.querySelectorAll(SEL).forEach(destroy);
  });

  function syncSplitShowcaseInView() {
    document.querySelectorAll(SEL).forEach(function (root) {
      if (!root.dataset.splitShowcaseRevealInit) return;
      if (root.classList.contains(REVEALED)) return;
      var r = root.getBoundingClientRect();
      var vw = window.innerWidth || 0;
      var vh = window.innerHeight || 1;
      var interLeft = Math.max(r.left, 0);
      var interRight = Math.min(r.right, vw);
      var interTop = Math.max(r.top, 0);
      var interBottom = Math.min(r.bottom, vh);
      var w = Math.max(0, interRight - interLeft);
      var hVis = Math.max(0, interBottom - interTop);
      var elArea = Math.max(1, r.width * r.height);
      var interArea = w * hVis;
      var ir = elArea > 0 ? interArea / elArea : 0;
      var entry = { boundingClientRect: r, intersectionRatio: ir, isIntersecting: w > 0 && hVis > 0 };
      if (entry.isIntersecting && shouldReveal(entry)) reveal(root);
    });
  }

  if (window.r3nuRegisterViewportRecheck) {
    window.r3nuRegisterViewportRecheck(syncSplitShowcaseInView);
  }
})();
